import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import css from '../toolbar.module.css';
import { useService } from '../../../../../../services/services';
import DateFilter, {
  DateFields,
} from '../../../../../../components/common/table/date-filter/date-filter';
import ExcelButtonNew from '../../../../../../components/common/table/excel-button/excel-button-new';
import ValueFilter from '../../../../../../components/common/table/value-filter/value-filter';
import ShowFilter from '../../../../../../components/common/table/show-filter/show-filter';
import { useQuery } from '../../../../../../hooks/use-query';

const DEBOUNCE_TIME = 3000;

export default function AnimalGroupToolbar() {
  const { t } = useTranslation();
  const transKeyPrefix = 'pages.livestock.animals_and_events.animals.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);
  const { farmId } = useParams<{ farmId: string }>();
  const { groupAnimalService } = useService();
  const [species, setSpecies] = useState([]);
  const [barns, setBarns] = useState([]);
  const [showFilter, setShowFilter] = useState(true);
  const [urlParams] = useQuery();

  useEffect(() => {
    const $species = groupAnimalService
      .species(farmId, urlParams)
      .subscribe((species) => setSpecies(species));
    const $barns = groupAnimalService
      .barns(farmId, urlParams)
      .subscribe((barns) => setBarns(barns));
    return () => {
      $species.unsubscribe();
      $barns.unsubscribe();
    };
  }, [groupAnimalService, farmId, urlParams]);

  return (
    <div className={css['animal-list-topbar']}>
      <div className={css['filter-bar-excel']}>
        <div className={css['filter-show']}>
          <label htmlFor="show-filter" className={css['filter-label']}></label>
          <ShowFilter
            onClick={() => setShowFilter(!showFilter)}
            defaultActiveValue={true}
          />
        </div>

        <div className={css['excel-export']}>
          <label htmlFor="excel-export" className={css['filter-label']}></label>
          <ExcelButtonNew
            onClick={() => {
              return groupAnimalService
                .exportXls(farmId, { ...urlParams })
                .subscribe();
            }}
          />
        </div>
      </div>
      {showFilter && (
        <div className={css['filter-bar']}>
          <div className={css['date-filter']}>
            <label htmlFor="date">{tp('animals_group_date')}</label>
            <DateFilter
              name={'animals-date'}
              fields={[DateFields.date]}
              showDateControls={false}
              defaultValue={new Date()}
              disableFutureDates={true}
              debounceTime={DEBOUNCE_TIME}
            />
          </div>

          <div className={css['species-filter']}>
            <label htmlFor="species" className={css['filter-label']}>
              {tp('species')}
            </label>
            <div className={css['species-filter-container']}>
              <ValueFilter
                name="species"
                options={species}
                className={'value-filter-width-160'}
                isMulti={true}
                showSelectedCountInsteadOfValue={true}
                closeMenuOnSelect={false}
                placeholder={
                  species.find((species) => species?.label === tp('pigs'))
                    ?.label ?? ''
                }
                isOptionDisabled={(option) => option.label === tp('pigs')}
              />
            </div>
          </div>

          <div className={css['barn-filter']}>
            <label htmlFor="barn_ldc_nr" className={css['filter-label']}>
              {tp('barn')}
            </label>
            <div className={css['barn-filter-container']}>
              <ValueFilter
                name="barn_ldc_nr"
                options={barns}
                className={'value-filter-width-160'}
                isMulti={true}
                showSelectedCountInsteadOfValue={true}
                closeMenuOnSelect={false}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
